import React, {FormEvent, useEffect, useState} from 'react';
import {useDispatch} from "react-redux";
import {
    getEmailsSettingForAccount,
    setEmailsSettingForAccount
} from "../../../../../../../store/actionCreators/accountEmailsActionCreators";
import {useTypedSelector} from "../../../../../../../hooks/useTypeSelector";
import {
    Box, Button, Grid,
    Modal, Typography,
} from "@mui/material";
import {PageSpinner} from "../../../../../../com/pagesTab/pagesSpinner/spinner";
import {makeStyles} from "@mui/styles";
import {ClosePopupBtn} from "../../../../../../../com/closePopupBtn/closePopupBtn";
import validator from "validator";
import {InputWithShowingResults} from "../../../../../../com/inputWithShowingResults/inputWithShowingResults";
import {SimpleConfirmPopup} from "../../../../../../../com/simpleConfirmPopup/SimpleConfirmPopup";
import {convertEmailsObjectToArray} from "../../../../../../../globals/globFunctions";

const useStyles = makeStyles(() => ({
    emailsSettingPopup: {
        width: `770px`,
        backgroundColor: `#ffffff`,
        height: `98vh`,
        display: `flex`,
        flexFlow: `column`,
        "& .MuiInputBase-input": {
            padding: `5px`,
            height: `20px`,
        },
    },
    emailsSettingPopupHeader: {
        padding: `6px 20px`,
        background: `linear-gradient(89.41deg, #0198D2 0%, #3540A8 100%)`,
        "& .MuiButton-root": {
            "& > svg": {
                "& > path": {
                    fill: `#ffffff`,
                },
            },
        },
    },
    buttonSave: {
        width: `100%`,
        // background: `linear-gradient(135.47deg, #DD5A01 3.9%, #F0CA21 100%)`,
        "&.MuiButton-root": {
            padding: `10px 0`,
            backgroundColor: `#014780`,
            "&:hover": {
                backgroundColor: `#01478080`,
            }
        },
        "&.Mui-disabled": {
            opacity: `0.5`,
        },
        "& > .MuiTypography-root": {
            lineHeight: `15px`,
        },
    },
}))

interface Props {
    show: boolean;
    onHide: () => void;
}

const AccountEmailSettingPopup: React.FC<Props> = ({
                                                       show,
                                                       onHide
                                                   }) => {
    const dispatch = useDispatch();
    const {accountEmailsState} = useTypedSelector((state) => state);
    const [inputValue, setInputValue] = useState<string>('');
    const [emails, setEmails] = useState<string[]>([]);
    const [isChangesNotSaved, changeStatusForNotSavedChanges] = useState(false);
    const classes = useStyles();

    useEffect(() => {
        dispatch(getEmailsSettingForAccount());
    }, []);

    useEffect(() => {
        const emailsArray = convertEmailsObjectToArray(accountEmailsState?.emails || {});
        setEmails(emailsArray);
        setInputValue("");
        changeStatusForNotSavedChanges(false);
    }, [accountEmailsState.emails]);

    const isEmailsStateWasChanged = (): boolean => {
        const emailsArrayDefault = convertEmailsObjectToArray(accountEmailsState?.emails || {});
        return JSON.stringify(emailsArrayDefault) !== JSON.stringify(emails);
    }
    const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setInputValue(value);
    }

    const checkValidValue = (): boolean => {
        return validator.isEmail(inputValue) || inputValue === '';
    };

    const isAlreadyExist = (): boolean => {
        return emails.some(email => email === inputValue);
    };

    const addEmail = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!checkValidValue() || emails.length >= 4 || isAlreadyExist() || !inputValue) {
            return;
        }
        setEmails([
            ...emails,
            inputValue
        ]);
        setInputValue("");
    }

    const removeEmail = (email: string) => {
        const newEmails = emails.filter(emailFromState => emailFromState !== email);
        setEmails(newEmails);
    }
    const onClosePopup = () => {
        if (isEmailsStateWasChanged()) {
            changeStatusForNotSavedChanges(true);
        } else {
            onHide();
        }
    };

    const onSaveEmails = () => {
        dispatch(setEmailsSettingForAccount({
            emailAddress1: emails[0] || '',
            emailAddress2: emails[1] || '',
            emailAddress3: emails[2] || '',
            emailAddress4: emails[3] || '',
        }));
        onHide();
    }



    return (
        <>
            <Modal
                open={show}
                sx={{
                    display: `flex`,
                    justifyContent: `center`,
                    alignItems: `center`,
                }}
            >
                {accountEmailsState.loading ?
                    <Box
                        sx={{
                            height: `100vh`,
                            width: `100vw`,
                            display: `flex`,
                            alignItems: `center`,
                            justifyContent: `center`,
                        }}
                    >
                        <PageSpinner/>
                    </Box>
                    :
                    <Box className={classes.emailsSettingPopup}>
                        <Box
                            className={classes.emailsSettingPopupHeader}
                        >
                            <Grid container alignItems={`center`}>
                                <Grid item xs={10}>
                                    <Typography color={`#ffffff`} fontWeight={700}>
                                        Email Settings
                                    </Typography>
                                </Grid>
                                <Grid item xs>
                                    <Box sx={{display: `flex`, justifyContent: `flex-end`}}>
                                        <ClosePopupBtn onClose={onClosePopup}/>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <Typography fontSize={`12px`} pt={`10px`} px={`20px`}>
                            This setting is designed for Account’s administrator.
                            <br/>
                            Enter the following parameters in order to create a working URL
                            that sends My List items and quantities to your online store.
                            <br/>
                            First see how the current color-coded default values form the
                            RESULT URL in the bottom of this screen.
                            <br/>
                            Then edit (or remove) the values as necessary in order to create
                            the RESULT URL that works for you.
                        </Typography>
                        <Box
                            sx={{
                                flex: `1 1`,
                                overflow: `auto`,
                            }}
                        >
                            <Box
                                px={`20px`} py={`10px`}
                                sx={{
                                    flex: `1 1`,
                                    overflow: `auto`,
                                    rowGap: "40px",
                                    display: "flex",
                                    flexFlow: "column"
                                }}
                            >
                                {/*main fields*/}
                                <InputWithShowingResults
                                    values={emails}
                                    addItem={addEmail}
                                    removeItem={removeEmail}
                                    inputValue={inputValue}
                                    fieldName={"email"}
                                    labelGridSizeNumber={4}
                                    labelCaption={'Contact Us Emails:'}
                                    onChangeInput={onChangeEmail}
                                    isValid={checkValidValue()}
                                    labelAlign={"end"}
                                />
                            </Box>
                        </Box>
                        <Box px={`20px`} py={`10px`}>
                            {/*save btn*/}
                            <Box>
                                <Grid
                                    container
                                    alignItems={`center`}
                                    justifyContent={`flex-end`}
                                >
                                    <Grid item xs={2}>
                                        <Box sx={{display: `flex`, alignItems: `center`}}>
                                            <Button
                                                className={classes.buttonSave}
                                                disabled={!isEmailsStateWasChanged()}
                                                onClick={onSaveEmails}
                                            >
                                                <Typography color={`#ffffff`}>Save</Typography>
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Box>
                }
            </Modal>
            {isChangesNotSaved && (
                <SimpleConfirmPopup
                    onClose={changeStatusForNotSavedChanges}
                    contentText={`Save edits before exit?`}
                    onConfirm={onSaveEmails}
                    buttonText={[`Save`, `Don't Save`, `Cancel`]}
                    onCloseWithoutSaving={onHide}
                />
            )}
        </>
    )
}

export default AccountEmailSettingPopup;